import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion, SurveyQuestionOption} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {createOption, createQuestion, createScreen} from "../shared/surveyQuestions.util";
import {newSurveyObjectId} from "../shared/surveyId.util";

export const createEditorOption = (): SurveyQuestionOption => ({
    ...createOption(),
    id: newSurveyObjectId(),
});

export const createEditorQuestion = (type: SurveyQuestionType): SurveyQuestion => {
    const question = createQuestion(type);

    return {
        ...question,
        id: newSurveyObjectId(),
        options: question.options?.map(option => ({...option, id: newSurveyObjectId()})),
    };
};

export const createEditorScreen = (type: SurveyQuestionType, languages: string[]): SurveyQuestion => ({
    ...createScreen(type, languages),
    id: newSurveyObjectId(),
});

// A copy never opens a section, in any format: the section belongs to the divider above the original.
export const duplicateEditorQuestion = (question: SurveyQuestion): SurveyQuestion => ({
    ...question,
    id: newSurveyObjectId(),
    visibilityRules: undefined,
    startsSection: undefined,
    sectionTitle: undefined,
    options: question.options?.map(option => ({...option, id: newSurveyObjectId()})),
});

/** Switching on drops the flag rather than storing false, so off and on again leaves the question exactly as it was. */
export const withSwitchedOff = (question: SurveyQuestion, off: boolean): SurveyQuestion => {
    const {switchedOff, ...rest} = question;
    return off ? {...rest, switchedOff: true} : rest;
};

export type EditorListItem =
    | {kind: 'question', question: SurveyQuestion}
    // `break-${id of the question below it}`, rebuilt on every render. `fixed` is the "Section 1" header, which never
    // moves. A break carries its section's title, so the title moves with the divider, never with a question.
    | {kind: 'break', id: string, fixed?: boolean, sectionTitle?: LocalizedText};

/** A fixed break first (section 1), then a break above every other startsSection question. A startsSection on the
 *  first question and a sectionTitle on a question that opens no section are ignored. */
export const toEditorListItems = (answerable: SurveyQuestion[]): EditorListItem[] => (
    answerable.flatMap((question, position): EditorListItem[] => {
        const item: EditorListItem = {kind: 'question', question};
        if (position > 0 && !question.startsSection) return [item];

        return [
            {
                kind: 'break',
                id: `break-${question.id ?? position}`,
                ...(position === 0 && {fixed: true}),
                ...(question.sectionTitle && {sectionTitle: question.sectionTitle}),
            },
            item,
        ];
    })
);

/** Back to questions: startsSection and sectionTitle on the question right below each break and on no other. A break
 *  with no question below it disappears, and its title with it — the fixed one too, when another break was dropped
 *  right below it. The items start with the fixed break, as toEditorListItems makes them. */
export const fromEditorListItems = (items: EditorListItem[]): SurveyQuestion[] => {
    const questions: SurveyQuestion[] = [];
    let opening: Extract<EditorListItem, {kind: 'break'}> | undefined;

    items.forEach(item => {
        if (item.kind === 'break') {
            opening = item;
            return;
        }

        const {startsSection, sectionTitle, ...question} = item.question;
        questions.push({
            ...question,
            ...(!!opening && questions.length > 0 && {startsSection: true}),
            ...(opening?.sectionTitle && {sectionTitle: opening.sectionTitle}),
        });
        opening = undefined;
    });

    return questions;
};
